import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Plus, Zap, Calendar, Trash2, Power, PowerOff, Loader2, PlayCircle, Target, RefreshCw } from "lucide-react";

interface AutomationTrigger {
  id: string;
  name: string;
  description: string;
  triggerType: string;
  triggerConfig: Record<string, any>;
  actionType: string;
  actionConfig: Record<string, any>;
  playbookId: string | null;
  isActive: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
  triggerCount?: number;
  successCount?: number;
  failureCount?: number;
  lastTriggeredAt?: Date | null;
  playbooks?: { name: string; icon: string };
}

interface Playbook {
  id: string;
  name: string;
  description: string;
  icon: string;
  steps: any[];
  userId: string;
}

const TRIGGER_TYPES = [
  { value: "schedule", label: "Schedule", icon: Calendar, description: "Run on a recurring schedule" },
  { value: "event", label: "Event", icon: Zap, description: "Trigger when something happens" },
  { value: "condition", label: "Condition", icon: Target, description: "Run when conditions are met" },
];

const ACTION_TYPES = [
  { value: "run_playbook", label: "Run Playbook", description: "Execute a playbook" },
  { value: "send_reminder", label: "Send Reminder", description: "Create a reminder notification" },
  { value: "create_task", label: "Create Task", description: "Generate a new task" },
  { value: "ai_analysis", label: "AI Analysis", description: "Run AI analysis on content" },
];

const EVENT_TYPES = [
  { value: "new_source", label: "New Source Added" },
  { value: "new_thread", label: "New Thread Created" },
  { value: "reminder_due", label: "Reminder Due" },
  { value: "capture_added", label: "Quick Capture Added" },
  { value: "task_completed", label: "Task Completed" },
];

export default function Automations() {
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("active");

  const [newTrigger, setNewTrigger] = useState({
    name: "",
    description: "",
    triggerType: "schedule",
    triggerConfig: {} as Record<string, any>,
    actionType: "run_playbook",
    actionConfig: {} as Record<string, any>,
    playbookId: "",
  });

  const { data: automations = [], isLoading } = useQuery<(AutomationTrigger & { playbooks?: { name: string; icon: string } })[]>({
    queryKey: ["/api/automations"],
  });

  const { data: playbooks = [] } = useQuery<Playbook[]>({
    queryKey: ["/api/playbooks"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof newTrigger) => {
      return apiRequest("POST", "/api/automations", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/automations"] });
      setIsCreateOpen(false);
      setNewTrigger({
        name: "",
        description: "",
        triggerType: "schedule",
        triggerConfig: {},
        actionType: "run_playbook",
        actionConfig: {},
        playbookId: "",
      });
      toast({ title: "Automation created successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to create automation", description: error.message, variant: "destructive" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return apiRequest("PATCH", `/api/automations/${id}`, { isActive: isActive ? "true" : "false" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/automations"] });
      toast({ title: "Automation updated" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/automations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/automations"] });
      toast({ title: "Automation deleted" });
    },
  });

  const activeAutomations = automations.filter(a => a.isActive === "true");
  const inactiveAutomations = automations.filter(a => a.isActive !== "true");

  const getTriggerIcon = (type: string) => {
    const trigger = TRIGGER_TYPES.find(t => t.value === type);
    return trigger?.icon || Zap;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold" data-testid="text-page-title">Automations</h1>
          <p className="text-muted-foreground mt-1">Set up triggers to automate your workflows</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-create-automation">
              <Plus className="h-4 w-4 mr-2" />
              New Automation
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Automation</DialogTitle>
              <DialogDescription>Set up an automatic trigger for your workflows</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  placeholder="e.g., Daily Research Summary"
                  value={newTrigger.name}
                  onChange={(e) => setNewTrigger(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="input-automation-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="What does this automation do?"
                  value={newTrigger.description}
                  onChange={(e) => setNewTrigger(prev => ({ ...prev, description: e.target.value }))}
                  data-testid="input-automation-description"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Trigger Type</Label>
                <Select
                  value={newTrigger.triggerType}
                  onValueChange={(value) => setNewTrigger(prev => ({ ...prev, triggerType: value, triggerConfig: {} }))}
                >
                  <SelectTrigger data-testid="select-trigger-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIGGER_TYPES.map(trigger => (
                      <SelectItem key={trigger.value} value={trigger.value}>
                        <div className="flex items-center gap-2">
                          <trigger.icon className="h-4 w-4" />
                          <div>
                            <div className="font-medium">{trigger.label}</div>
                            <div className="text-xs text-muted-foreground">{trigger.description}</div>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {newTrigger.triggerType === "schedule" && (
                <div className="space-y-2">
                  <Label>Schedule</Label>
                  <Select
                    value={newTrigger.triggerConfig.frequency || "daily"}
                    onValueChange={(value) => setNewTrigger(prev => ({ 
                      ...prev, 
                      triggerConfig: { ...prev.triggerConfig, frequency: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">Every Hour</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {newTrigger.triggerType === "event" && (
                <div className="space-y-2">
                  <Label>Event Type</Label>
                  <Select
                    value={newTrigger.triggerConfig.eventType || ""}
                    onValueChange={(value) => setNewTrigger(prev => ({ 
                      ...prev, 
                      triggerConfig: { ...prev.triggerConfig, eventType: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select event..." />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map(event => (
                        <SelectItem key={event.value} value={event.value}>{event.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Action Type</Label>
                <Select
                  value={newTrigger.actionType}
                  onValueChange={(value) => setNewTrigger(prev => ({ ...prev, actionType: value }))}
                >
                  <SelectTrigger data-testid="select-action-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTION_TYPES.map(action => (
                      <SelectItem key={action.value} value={action.value}>
                        <div>
                          <div className="font-medium">{action.label}</div>
                          <div className="text-xs text-muted-foreground">{action.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {newTrigger.actionType === "run_playbook" && (
                <div className="space-y-2">
                  <Label>Select Playbook</Label>
                  <Select
                    value={newTrigger.playbookId}
                    onValueChange={(value) => setNewTrigger(prev => ({ ...prev, playbookId: value }))}
                  >
                    <SelectTrigger data-testid="select-playbook">
                      <SelectValue placeholder="Choose a playbook..." />
                    </SelectTrigger>
                    <SelectContent>
                      {playbooks.map(playbook => (
                        <SelectItem key={playbook.id} value={playbook.id}>
                          {playbook.icon} {playbook.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
              <Button 
                onClick={() => createMutation.mutate(newTrigger)}
                disabled={!newTrigger.name || !newTrigger.triggerType || createMutation.isPending}
                data-testid="button-save-automation"
              >
                {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Automation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <Power className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeAutomations.length}</p>
                <p className="text-sm text-muted-foreground">Active Automations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <RefreshCw className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {automations.reduce((sum, a) => sum + (a.triggerCount || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Total Executions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <PlayCircle className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{playbooks.length}</p>
                <p className="text-sm text-muted-foreground">Available Playbooks</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="active" data-testid="tab-active">
            Active ({activeAutomations.length})
          </TabsTrigger>
          <TabsTrigger value="inactive" data-testid="tab-inactive">
            Inactive ({inactiveAutomations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          {activeAutomations.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Zap className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No Active Automations</h3>
                <p className="text-sm text-muted-foreground mb-4">Create an automation to get started</p>
                <Button onClick={() => setIsCreateOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Automation
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {activeAutomations.map(automation => {
                const TriggerIcon = getTriggerIcon(automation.triggerType);
                return (
                  <Card key={automation.id} data-testid={`card-automation-${automation.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <TriggerIcon className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{automation.name}</CardTitle>
                            <CardDescription>{automation.description || "No description"}</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={automation.isActive === "true"}
                            onCheckedChange={(checked) => toggleMutation.mutate({ id: automation.id, isActive: checked })}
                            data-testid={`switch-automation-${automation.id}`}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => deleteMutation.mutate(automation.id)}
                            data-testid={`button-delete-automation-${automation.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <Badge variant="outline">
                          {TRIGGER_TYPES.find(t => t.value === automation.triggerType)?.label || automation.triggerType}
                        </Badge>
                        <span>→</span>
                        <Badge variant="secondary">
                          {ACTION_TYPES.find(a => a.value === automation.actionType)?.label || automation.actionType}
                        </Badge>
                        {automation.playbooks && (
                          <>
                            <span>→</span>
                            <Badge variant="outline">{automation.playbooks.icon} {automation.playbooks.name}</Badge>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span>Triggered {automation.triggerCount || 0} times</span>
                        <span>Success: {automation.successCount || 0}</span>
                        <span>Failed: {automation.failureCount || 0}</span>
                        {automation.lastTriggeredAt && (
                          <span>Last: {new Date(automation.lastTriggeredAt).toLocaleDateString()}</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="inactive">
          {inactiveAutomations.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <PowerOff className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No Inactive Automations</h3>
                <p className="text-sm text-muted-foreground">All your automations are active</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {inactiveAutomations.map(automation => {
                const TriggerIcon = getTriggerIcon(automation.triggerType);
                return (
                  <Card key={automation.id} className="opacity-60" data-testid={`card-automation-${automation.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-muted">
                            <TriggerIcon className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{automation.name}</CardTitle>
                            <CardDescription>{automation.description || "No description"}</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={false}
                            onCheckedChange={(checked) => toggleMutation.mutate({ id: automation.id, isActive: checked })}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => deleteMutation.mutate(automation.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
